/**
 * FB-20 — the Parties step "Help" modal: a worked, example-led "who sees what"
 * overlay for the cross-tenant sharing model.
 *
 * Copy is drawn from `.vibe/_CROSS_TENANT/05_COMPANY_VISIBILITY.md`
 * (Layer 1 tenant-to-tenant visibility + Layer 2 per-project / per-contract
 * access, XT-D20..D23) and kept concise + scannable — this is a help overlay,
 * not a spec dump. Pure presentational; no data layer.
 */
import { type ReactElement } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const ROLE_ACCESS: readonly { role: string; sees: string }[] = [
  {
    role: "Project Customer",
    sees: "The project + its state + published reports only (no editing).",
  },
  {
    role: "Project Contractor",
    sees: "Project, contracts & positions; can run inspection instructions, evaluate data acquisitions, and view final reports.",
  },
  {
    role: "Contract Customer",
    sees: "Contract-scoped: the project shell (stats filtered to their contract) + that contract's reports.",
  },
  {
    role: "Contract Contractor",
    sees: "Contract-scoped: that contract with its positions, inspection instructions, data acquisitions & reports.",
  },
  {
    role: "Reporting Subcontractor",
    sees: "Project-wide: view project + inspection instructions (read-only), evaluate data acquisitions, create/edit reports; no contracts.",
  },
];

export function PartiesHelpDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="parties-help-title"
    >
      <DialogTitle id="parties-help-title">
        Who sees what — sharing a project with partners
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText component="div">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Every project lives in your tenant. When you name another company as
            a party, what they can see depends on the <strong>role</strong> you
            give them.
          </Typography>

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Naming a <strong>partner company (tenant)</strong> on a party field{" "}
            <strong>automatically shares</strong> the project (or that contract)
            with them, scoped to the role. Picking a plain{" "}
            <strong>contact</strong> instead is just an address for
            reports/invoices — it <strong>shares nothing</strong>.
          </Alert>

          <Typography variant="subtitle2" gutterBottom>
            A worked example
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            <Box component="span" sx={{ display: "block" }}>
              You own <strong>Project X</strong>. You set:
            </Box>
            <Box
              component="ul"
              sx={{ mt: 1, mb: 0, pl: 3, "& li": { mb: 0.75 } }}
            >
              <li>
                <strong>Project Customer = Acme</strong> (a tenant) → Acme sees
                Project X and its <strong>published reports</strong>, but cannot
                edit anything.
              </li>
              <li>
                <strong>Project Contractor = Foo</strong> (a tenant) → Foo can
                run the inspection instructions and{" "}
                <strong>evaluate the data acquisitions</strong>.
              </li>
              <li>
                One park&apos;s <strong>Contract Customer = Bar</strong> (a
                tenant) → Bar sees <strong>only that contract&apos;s</strong>{" "}
                reports — not the rest of the project.
              </li>
            </Box>
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Role → access at a glance
          </Typography>
          <Table size="small" aria-label="Role access summary">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>What they can see / do</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ROLE_ACCESS.map((r) => (
                <TableRow key={r.role}>
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                    {r.role}
                  </TableCell>
                  <TableCell>{r.sees}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2 }}
          >
            Customer roles never see the subcontractor chain (who performs or
            reports). Contract-scoped roles see only their own contract; the
            project shell still appears, but stats roll up over their contract
            only.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Got it</Button>
      </DialogActions>
    </Dialog>
  );
}
